// TES-955 (TES-941 "§LÄKARADMIN SKA UTGÅ FRÅN PATIENTEN") — every medical
// workflow lives under the patient, in tabs that are part of the URL. This is
// the one place that knows how those URLs are spelled, so the meeting list,
// the Today page, the redirect from the old meeting route and the patient
// page itself cannot drift apart.
//
// Pure on purpose: no router, no React, no path aliases. The unit tests in
// `apps/admin/test` run it with plain `node --test`.

// The tab vocabulary, shared by the URL reader and the tab bar.
export const PATIENT_TABS = [
  "oversikt",
  "overtag",
  // TES-506 — "Vårdmöte under patienten som en flik". TES-955: the meeting
  // itself opens inside this tab.
  "moten",
  // TES-519 — the renewal decision lives here, not in the work-queue row.
  "manad",
  "blodprov",
  "veckor",
  "chat",
  "test",
] as const;
export type PatientTab = (typeof PATIENT_TABS)[number];

export function isPatientTab(value: string): value is PatientTab {
  return (PATIENT_TABS as readonly string[]).includes(value);
}

/** The query parameter carrying the tab. Absent means "oversikt". */
export const TAB_PARAM = "flik";
/** The query parameter carrying the open care meeting. */
export const MEETING_PARAM = "mote";
/**
 * TES-1245 — a one-shot request to open a work step on arrival. The
 * onboarding work item links with `?steg=beslut`, which opens the treatment
 * decision workspace once and is then dropped from the URL. Must match the
 * API's onboarding lane (work-queue.service.ts).
 */
export const STEP_PARAM = "steg";
export const DECISION_STEP = "beslut";

/**
 * `/patients/:id`, on a tab, optionally with a meeting open. The meeting
 * travels with every tab — not only "moten" — so a doctor who glances at the
 * overview mid-call comes back to the same meeting (and the same call).
 */
export function patientHref(
  patientId: string,
  tab: PatientTab = "oversikt",
  meetingId?: string | null,
): string {
  const params = new URLSearchParams();
  if (tab !== "oversikt") params.set(TAB_PARAM, tab);
  if (meetingId) params.set(MEETING_PARAM, meetingId);
  const query = params.toString();
  return `/patients/${encodeURIComponent(patientId)}${query ? `?${query}` : ""}`;
}

/**
 * TES-1102 — the patient page as an admin surface (Support inbox, booking
 * diagnostics) links to it: an admin reads patients in her read-only doctor
 * view (TES-1040), a coach with the admin grant in her own coach view. The
 * prefix mirrors SUPPORT_VIEW_PREFIX.doctor in support-view.ts (a runtime
 * import of a sibling module is what this file cannot take).
 */
export function adminPatientHref(
  actingRole: string | undefined,
  patientId: string,
): string {
  const href = patientHref(patientId);
  return actingRole === "admin" ? `/support/doctor${href}` : href;
}

/**
 * TES-1070 — the patient's treatment area on "Översikt", where
 * "Bekräfta receptstatus" sits (TES-1150). A stable link for the overview's "Nästa steg" prompt. `?flik=oversikt` is explicit so a transfer patient
 * is not sent on to "overtag" (transferLandingTab only redirects a doctor who
 * asked for no tab); the hash is an anchor the panel scrolls itself to. Must
 * match TREATMENT_START_ANCHOR in treatment-start.ts.
 */
export function patientTreatmentStartHref(patientId: string): string {
  return `/patients/${encodeURIComponent(patientId)}?${TAB_PARAM}=oversikt#behandlingsstart`;
}

/** The patient's "Vårdmöten" tab with one meeting open. */
export function patientMeetingHref(
  patientId: string,
  meetingId: string,
): string {
  return patientHref(patientId, "moten", meetingId);
}

/** The meeting open in this query string, if any. */
export function openMeetingId(search: URLSearchParams | string): string | null {
  const params =
    typeof search === "string" ? new URLSearchParams(search) : search;
  const id = params.get(MEETING_PARAM)?.trim();
  return id ? id : null;
}

/** The same query with another tab; everything else (the open meeting, the
 *  chat channel) is kept. */
export function withTab(
  prev: URLSearchParams,
  tab: PatientTab,
): URLSearchParams {
  const params = new URLSearchParams(prev);
  if (tab === "oversikt") params.delete(TAB_PARAM);
  else params.set(TAB_PARAM, tab);
  return params;
}

/**
 * TES-1034 (Emelie's point 7) — the transfer patient's landing tab.
 *
 * A transfer patient opens on "overtag" when the doctor arrived without asking
 * for a tab. That used to be re-checked on every render, and choosing
 * "Översikt" REMOVES `?flik` (it is the default), so the page read that as
 * "arrived without a tab" and sent her straight back to "overtag": the
 * overview could not be reached from the transfer workspace.
 *
 * Now the decision is taken once per review, on the first arrival: `handled`
 * is the review id this page has already landed for. Returns the tab to switch
 * to (or null), and the review id to remember as handled.
 */
export function transferLandingTab(input: {
  reviewId: string | null;
  askedForTab: boolean;
  handled: string | null;
}): { tab: PatientTab | null; handled: string | null } {
  const { reviewId, askedForTab, handled } = input;
  if (!reviewId || handled === reviewId) return { tab: null, handled };
  return { tab: askedForTab ? null : "overtag", handled: reviewId };
}

/** Open a meeting in the "Vårdmöten" tab, or (null) close it back to the
 *  list of the patient's meetings. */
export function withMeeting(
  prev: URLSearchParams,
  meetingId: string | null,
): URLSearchParams {
  const params = withTab(prev, "moten");
  if (meetingId) params.set(MEETING_PARAM, meetingId);
  else params.delete(MEETING_PARAM);
  return params;
}

/**
 * Is a care meeting on screen? The meeting needs the full width of the
 * content area (big video, note beside it); every other page keeps the
 * reading measure.
 */
export function isMeetingOnScreen(pathname: string, search: string): boolean {
  if (!/^\/patients\/[^/]+\/?$/.test(pathname)) return false;
  const params = new URLSearchParams(search);
  return params.get(TAB_PARAM) === "moten" && openMeetingId(params) !== null;
}

/**
 * TES-1335 (admin decision 2026-10-13, "ARBETSKÖ FÖR SVAR PÅ LÄKARENS
 * FRÅGA") — "Starta medicinsk åtgärd": each action is a direct link to the
 * function on the patient page, never the bare patient page the doctor
 * would have to search. The hash is an anchor the page scrolls to once it
 * has loaded (PatientDetail). Mirrors DOCTOR_QUESTION_MEDICAL_ACTIONS in
 * contracts, restated because this module takes no imports.
 */
export type MedicalActionKey =
  | "request_completion"
  | "propose_meeting"
  | "prescription_review"
  | "treatment_plan"
  | "register_medical_case";

export const MEDICAL_ACTION_ANCHOR: Record<MedicalActionKey, string> = {
  request_completion: "komplettering",
  propose_meeting: "boka-vardmote",
  prescription_review: "receptbedomning",
  treatment_plan: "behandlingsplan",
  register_medical_case: "medicinskt-arende",
};

const MEDICAL_ACTION_TAB: Record<MedicalActionKey, PatientTab | null> = {
  request_completion: "oversikt",
  propose_meeting: "oversikt",
  // Which review that is depends on the patient: the page decides
  // (prescriptionReviewLanding).
  prescription_review: null,
  treatment_plan: "blodprov",
  register_medical_case: "oversikt",
};

export function medicalActionHref(
  patientId: string,
  action: MedicalActionKey,
  answerTaskId?: string,
): string {
  const params = new URLSearchParams();
  const tab = MEDICAL_ACTION_TAB[action];
  if (tab) params.set(TAB_PARAM, tab);
  if (answerTaskId) {
    params.set(ANSWER_PARAM, answerTaskId);
    params.set(ANSWER_ACTION_PARAM, action);
  }
  const query = params.toString() ? `?${params.toString()}` : "";
  return `/patients/${encodeURIComponent(patientId)}${query}#${MEDICAL_ACTION_ANCHOR[action]}`;
}

/**
 * TES-1400 (Ö-576, Emelie 2026-10-15) — "Ett patientsvar blir Hanterat först
 * när läkaren markerat det hanterat eller skapat den spårbara åtgärd som
 * svaret leder till." The queue's "Starta medicinsk åtgärd" opens the
 * function with the answer it came from (`svar`) and the action chosen
 * (`atgard`); the patient page marks the answer handled only when that
 * action is actually created there. Leaving the page leaves it open.
 */
export const ANSWER_PARAM = "svar";
export const ANSWER_ACTION_PARAM = "atgard";

export function pendingAnswerActionFrom(
  search: string,
): { taskId: string; action: MedicalActionKey } | null {
  const params = new URLSearchParams(search);
  const taskId = params.get(ANSWER_PARAM);
  const action = params.get(ANSWER_ACTION_PARAM);
  if (!taskId || !action || !(action in MEDICAL_ACTION_ANCHOR)) return null;
  return { taskId, action: action as MedicalActionKey };
}

/** Whether a hash names one of the medical actions' anchors. */
export function isMedicalActionAnchor(anchor: string): boolean {
  return (Object.values(MEDICAL_ACTION_ANCHOR) as string[]).includes(anchor);
}

/**
 * "Öppna receptbedömning": the prescription review the patient has open —
 * her prescription transfer review while there is one, otherwise the monthly check-in's
 * renewal decision, otherwise "Bekräfta receptstatus" on the overview
 * (TES-1070, `#behandlingsstart`).
 */
export function prescriptionReviewLanding(input: {
  hasTransferReview: boolean;
  hasRenewalReview: boolean;
}): { tab: PatientTab; anchor: string | null } {
  if (input.hasTransferReview) return { tab: "overtag", anchor: null };
  if (input.hasRenewalReview) return { tab: "manad", anchor: null };
  return { tab: "oversikt", anchor: "behandlingsstart" };
}
